import React, { useContext, useState, useMemo, useEffect } from "react";
import { UserGlobalContextMemorySpace } from "../../../contexts/user/userGlobalContext";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { NavLink, useLocation } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { Row, Col } from "react-bootstrap";
import { formatDate } from '../../../utils/format_date'
import { calculateAge } from '../../../utils/calculate_old'
import FaUserCircle from "../../../assets/img/hb/Perfil Menú.svg";
import MisTramitesIcon from "../../../assets/img/hb/MisTramitesMenu.svg";
import IconPlanDeSalud from "../../../assets/img/iconPlanDeSalud.svg";

// PBI-25040 (rework): mapeo ruta -> sección del acordeón. Se usa para que el
// cierre automático solo aplique al cambiar a OTRO ítem del menú, no al
// navegar entre hijos de la misma sección ya abierta. Las rutas
// /debito-automatico y /debito-cuenta están ocultas del menú (ver más abajo)
// pero se incluyen igual para que Facturación no quede en estado inconsistente
// si se llega a ellas por otra vía (deep link, notificación, etc).
const PERFIL_ROUTES = ["/perfil", "/cambiar-contrasena"];
const MI_SALUD_ROUTES = ["/recetas", "/epicrisis", "/ordenes-medicas", "/notas-medicas"];
const MIS_TRAMITES_ROUTES = ["/adjuntar-documentacion", "/mis-autorizaciones", "/certificado-asistencia"];
const FACTURACION_ROUTES = ["/facturas", "/debito-automatico", "/debito-cuenta", "/historial-debito"];
const REINTEGRO_ROUTES = ["/reintegro-estado", "/reintegro-solicitud"];
const PLAN_DE_SALUD_ROUTES = [...FACTURACION_ROUTES, ...REINTEGRO_ROUTES, "/credencial-digital"];

const DrawerHB = () => {
  const { sitePSHB, setSitePSHB, userPSHB, setUserPSHB, user, setUser, credencial, loadCredencial, hasAutorizaciones } =
    useContext(UserGlobalContextMemorySpace);
  const [isActivePerfil, setIsActivePerfil] = useState(false);
  const [isActiveReintegro, setIsActiveReintegro] = useState(false);
  const [isActiveMiSalud, setIsActiveMiSalud] = useState(false);
  const [isActiveMisTramites, setIsActiveMisTramites] = useState(false);
  const [isActiveFacturacion, setIsActiveFacturacion] = useState(false);
  const [isActivePlanDeSalud, setIsActivePlanDeSalud] = useState(false)
  const [state, setState] = useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });
  const location = useLocation();

  // PBI-25040 (rework): el cierre automático debe aplicar solo al cambiar a
  // OTRO ítem del menú lateral, no al navegar entre hijos de una sección ya
  // abierta. Si la sección ya estaba abierta y la nueva ruta le pertenece, se
  // mantiene abierta; en cualquier otro caso se cierra. No se fuerza apertura
  // por deep link (arranca cerrado si el usuario no tocó el menú).
  useEffect(() => {
    const path = location.pathname;
    setIsActivePerfil((prev) => prev && PERFIL_ROUTES.includes(path));
    setIsActiveMiSalud((prev) => prev && MI_SALUD_ROUTES.includes(path));
    setIsActiveMisTramites((prev) => prev && MIS_TRAMITES_ROUTES.includes(path));
    setIsActivePlanDeSalud((prev) => prev && PLAN_DE_SALUD_ROUTES.includes(path));
    setIsActiveFacturacion((prev) => prev && FACTURACION_ROUTES.includes(path));
    setIsActiveReintegro((prev) => prev && REINTEGRO_ROUTES.includes(path));
  }, [location.pathname]);

  const { user: {
    data: { Customers: {
      firstName,
      lastName,
      socialSecurityNumber,
      email,
      socialSecurityPlan,
      dni,
      birthday
    } },
  } } = user

  const ivaInfo = useMemo(() => {
    if (!credencial || !credencial.IvaTax) return null;
    const infoTax = credencial.IvaTax === 'N' ? 'EXENTO' : 'GRAVADO';
    return infoTax;
  }, [credencial?.IvaTax]);

  // Bug QA (PBI-24883): Facturación y Reintegros son mutuamente excluyentes
  // dentro de "Plan de Salud" — abrir uno cierra el otro.
  const handleToggleFacturacion = () => {
    setIsActiveFacturacion((prev) => {
      const next = !prev;
      if (next) setIsActiveReintegro(false);
      return next;
    });
  };

  const handleToggleReintegro = () => {
    setIsActiveReintegro((prev) => {
      const next = !prev;
      if (next) setIsActiveFacturacion(false);
      return next;
    });
  };

  // PBI-24883 (nuevo requerimiento): los acordeones de PRIMER NIVEL del drawer
  // (Perfil, Mi Salud, Plan de Salud) son mutuamente excluyentes — solo uno
  // puede estar abierto a la vez. Al abrir uno se cierran los otros, y si el
  // que se cierra es "Plan de Salud" también se resetean sus subítems
  // (Facturación / Reintegros) para mantener consistencia con el fix anterior.
  const handleTogglePerfil = () => {
    setIsActivePerfil((prev) => {
      const next = !prev;
      if (next) {
        setIsActiveMiSalud(false);
        setIsActivePlanDeSalud(false);
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
        setIsActiveMisTramites(false);
      }
      return next;
    });
  };

  const handleToggleMiSalud = () => {
    setIsActiveMiSalud((prev) => {
      const next = !prev;
      if (next) {
        setIsActivePerfil(false);
        setIsActivePlanDeSalud(false);
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
        setIsActiveMisTramites(false);
      }
      return next;
    });
  };

  // Bug QA (PBI-24883): al colapsar el grupo "Plan de Salud" (plegando su propio
  // acordeón, sin cerrar todo el drawer, o porque se abrió otro acordeón de
  // primer nivel) se resetean sus subítems.
  const handleTogglePlanDeSalud = () => {
    setIsActivePlanDeSalud((prev) => {
      const next = !prev;
      if (next) {
        setIsActivePerfil(false);
        setIsActiveMiSalud(false);
        setIsActiveMisTramites(false);
      } else {
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
      }
      return next;
    });
  };

  // PBI-25040 (rework): "Mis Trámites" quedaba fuera del grupo de exclusión
  // mutua de los acordeones de primer nivel (Perfil, Mi Salud, Plan de Salud).
  // Se agrega el mismo patrón que los demás: al abrir, cierra el resto.
  const handleToggleMisTramites = () => {
    setIsActiveMisTramites((prev) => {
      const next = !prev;
      if (next) {
        setIsActivePerfil(false);
        setIsActiveMiSalud(false);
        setIsActivePlanDeSalud(false);
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
      }
      return next;
    });
  };



  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    // Al cerrar el drawer (por cualquier vía: navegación real que bubblea
    // hasta el Box raíz, click afuera o Escape vía onClose de MUI Drawer),
    // colapsamos también los dropdowns internos de Plan de Salud para que
    // no queden expandidos de una sesión anterior la próxima vez que se abra.
    if (!open) {
      setIsActiveFacturacion(false);
      setIsActiveReintegro(false);
      setIsActivePlanDeSalud(false);
    }

    setState({ ...state, [anchor]: open });
  };

  const handleRedirect = () => {
    window.open("https://www.hospitalbritanico.org.ar/", "_blank");
  };

  const truncateEmail = (email) => {
    if (!email) return '';
    return email.length > 23 ? email.substring(0, 23) + '...' : email;
  };

  const list = (anchor) => (
    <Box
      sx={{ width: anchor === "top" || anchor === "bottom" ? "auto" : "auto" }}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
      className="drawer"
    >
      <div style={{ minHeight: "100vh", backgroundColor: "#efefef" }}>
        <aside className="asideWebHBMobile">
          <div className="d-flex flex-column" style={{ width: "100%" }}>
            <div
              className="d-flex align-items-center margin"
              style={{ backgroundColor: "#efefef", paddingBottom: "25px", marginTop: "10px", }}
            >
              {/* <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "40px",
                  marginRight: "14px",
                  marginLeft: "30px",
                  color: "#224A8D"
                }}
              >account_circle</span> */}

                <img
              src={FaUserCircle}
              alt="..."
              style={{
                height: "40px",
                width: "40px",
                marginRight: "14px",
                marginLeft: "30px",
              }}
            />
              <div>
                <div
                  style={{
                    color: "#224A8D",
                    fontFamily: "Roboto-Bold",
                    marginRight: "10px",
                    fontSize: "17px",
                  }}
                >
                  {firstName}
                  <br />
                  {lastName}
                </div>
                <div className="text_old_years">
                  {calculateAge(birthday)}
                </div>
              </div>
            </div>
            <ul className="items_card_container">
              <li>Email: <span>{truncateEmail(email)}</span></li>
              <li>Plan: <span>{socialSecurityPlan}</span></li>
              <li>N° afiliado: <span>{socialSecurityNumber}</span></li>
              {credencial && <li>IVA: <span>{loadCredencial ? 'Verificando Plan...' : ivaInfo}</span></li>}
              {credencial && <li>Fecha de ingreso: <span>{loadCredencial ? 'Verificando Plan...' : formatDate({ date: credencial.FechaIngreso, view_time: false })}</span></li>}
            </ul>
            <div className="separadorTitulo" style={{ width: "100%" }} />
            <div
              className="d-flex flex-column justify-content-center"
              style={{ width: "100%", backgroundColor: "#efefef" }}
            >
              <NavLink
                className="navigate-button"
                to="/"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>home</span>{" "}
                  <span>Inicio</span>
                </div>
              </NavLink>
              <div className="separadorItem" />
              <Row
                style={{ marginLeft: 0 }}
                onClick={(e) => {
                  if (
                    e.target.tagName.toLowerCase() === "svg" ||
                    e.target.tagName.toLowerCase() === "polyline"
                  ) {
                    e.stopPropagation();
                  }
                }}
              >
                <NavLink
                  className="navigate-button perfil-bottom"
                  to={"/perfil"}
                  activeclassname="active"
                  style={{ width: "72%" }}
                  onClick={(e) => {
                    if (
                      e.target.tagName.toLowerCase() === "svg" ||
                      e.target.tagName.toLowerCase() === "polyline"
                    ) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <Col md={10}>
                    <div className="d-flex textoMenuLateral">
                      <div>
                        <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>account_circle</span>{" "}
                        <span>Perfil</span>
                      </div>
                    </div>
                  </Col>
                </NavLink>
                <Col style={{ margin: "auto", marginLeft: "10px" }}>
                  {isActivePerfil ? (
                    <FiChevronUp
                      size={20}
                      onClick={handleTogglePerfil}
                    />
                  ) : (
                    <FiChevronDown
                      size={20}
                      onClick={handleTogglePerfil}
                    />
                  )}
                </Col>
              </Row>
              {isActivePerfil ? (
                <NavLink
                  className="navigate-button menuMobile"
                  to={"/cambiar-contrasena"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>
                      Cambiar contraseña
                    </span>
                  </div>
                </NavLink>
              ) : null}
              <div className="separadorItem" />
              <NavLink
                className="navigate-button"
                to="/grupo-familiar"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>diversity_4</span>{" "}
                  <span>Grupo familiar</span>
                </div>
              </NavLink>
              <div className="separadorItem" />
              <NavLink
                className="navigate-button"
                to="/resultados"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>inventory</span>{" "}
                  <span>Resultados</span>
                </div>
              </NavLink>
              <div className="separadorItem" />
              <NavLink
                className="navigate-button"
                to="/turnos"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>calendar_today</span>{" "}
                  <span>Turnos</span>
                </div>
              </NavLink>
              <div className="separadorItem" />
              <Row
              style={{ marginLeft: 0 }}
              onClick={(e) => {
                if (
                  e.target.tagName.toLowerCase() === "svg" ||
                  e.target.tagName.toLowerCase() === "polyline"
                ) {
                  e.stopPropagation();
                }
              }}
            >
              <div
                className="navigate-button perfil-bottom"
                activeclassname="active"
                style={{ width: "72%" }}
                onClick={(e) => {
                  if (
                    e.target.tagName.toLowerCase() === "svg" ||
                    e.target.tagName.toLowerCase() === "polyline"
                  ) {
                    e.stopPropagation();
                  }
                  handleToggleMiSalud();
                }}
              >
                <Col md={10}>
                  <div className="d-flex textoMenuLateral">
                    <div>
                      <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>assignment</span>{" "}
                      <span>Mi Salud</span>
                    </div>
                  </div>
                </Col>
              </div>
              <Col style={{ margin: "auto", marginLeft: "10px" }}>
                {isActiveMiSalud ? (
                  <FiChevronUp
                    size={20}
                    onClick={handleToggleMiSalud}
                  />
                ) : (
                  <FiChevronDown
                    size={20}
                    onClick={handleToggleMiSalud}
                  />
                )}
              </Col>
            </Row>
            {isActiveMiSalud ? (
              <>
                <NavLink
                  className="navigate-button"
                  to={"/recetas"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Recetas</span>
                  </div>
                </NavLink>
                <NavLink
                  className="navigate-button"
                  to={"/epicrisis"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Epicrisis</span>
                  </div>
                </NavLink>
                <NavLink
                  className="navigate-button"
                  to={"/ordenes-medicas"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Ordenes clínicas</span>
                  </div>
                </NavLink>
                <NavLink
                  className="navigate-button"
                  to={"/notas-medicas"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Notas Medicas</span>
                  </div>
                </NavLink>
              </>
            ) : null}
              <div className="separadorItem" />
              {/* <NavLink
                className="navigate-button"
                to="/recetas"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>prescription</span>{" "}
                  <span>Recetas</span>
                </div>
              </NavLink> */}
              <Row
                style={{ marginLeft: 0 }}
                onClick={(e) => {
                  if (
                    e.target.tagName.toLowerCase() === "svg" ||
                    e.target.tagName.toLowerCase() === "polyline"
                  ) {
                    e.stopPropagation();
                  }
                }}
              >
                <div
                  className="navigate-button perfil-bottom"
                  activeclassname="active"
                  style={{ width: "72%" }}
                  onClick={(e) => {
                    if (
                      e.target.tagName.toLowerCase() === "svg" ||
                      e.target.tagName.toLowerCase() === "polyline"
                    ) {
                      e.stopPropagation();
                    }
                    handleToggleMisTramites();
                  }}
                >
                  <Col md={10}>
                    <div className="d-flex textoMenuLateral">
                      <div>
                        <img src={MisTramitesIcon} className="iconosAtencion" alt="Mis trámites" style={{ width: "24px", height: "24px" }} />{" "}
                        <span>Mis trámites</span>
                      </div>
                    </div>
                  </Col>
                </div>
                <Col style={{ margin: "auto", marginLeft: "10px" }}>
                  {isActiveMisTramites ? (
                    <FiChevronUp
                      size={20}
                      onClick={handleToggleMisTramites}
                    />
                  ) : (
                    <FiChevronDown
                      size={20}
                      onClick={handleToggleMisTramites}
                    />
                  )}
                </Col>
              </Row>
              {isActiveMisTramites ? (
                <>
                  <NavLink
                    className="navigate-button"
                    to="/adjuntar-documentacion"
                    activeclassname="active"
                  >
                    <div className="d-flex textoMenuLateral">
                      <span style={{ paddingLeft: "30px" }}>Adjuntar documentación</span>
                    </div>
                  </NavLink>
                  {hasAutorizaciones && (
                    <NavLink
                      className="navigate-button"
                      to="/mis-autorizaciones"
                      activeclassname="active"
                    >
                      <div className="d-flex textoMenuLateral">
                        <span style={{ paddingLeft: "30px" }}>Mis autorizaciones</span>
                      </div>
                    </NavLink>
                  )}
                  <NavLink
                    className="navigate-button"
                    to="/certificado-asistencia"
                    activeclassname="active"
                  >
                    <div className="d-flex textoMenuLateral">
                      <span style={{ paddingLeft: "30px" }}>Certificados</span>
                    </div>
                  </NavLink>
                </>
              ) : null}
              <div className="separadorItem" />
              <a
                className="navigate-button"
                activeclassname="active"
                onClick={handleRedirect}
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>health_cross</span>{" "}
                  <span>Hospital Británico</span>
                </div>
              </a>
              <div className="separadorItem" />
              <NavLink
                className="navigate-button"
                to="/atencion-al-paciente"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>support_agent</span>{" "}
                  <span>Atención al paciente</span>
                </div>
              </NavLink>
              <div className="separadorItem" />
              <NavLink
                className="navigate-button"
                to="/micrositio"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>help_outline</span>{" "}
                  <span>Central de ayuda</span>
                </div>
              </NavLink>

              <div className="separadorItem" />
              {/* <NavLink
                className="navigate-button"
                to={"/micrositio"}
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <img src={FaAtencion} className="iconosAtencion" alt="..." />{" "}
                  <span>Central de Ayuda</span>
                </div>
              </NavLink> */}
              <div className="separadorItem" />
              {/* <NavLink
                className="navigate-button"
                to={"/ayuda"}
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <img
                    src={FaAyuda}
                    className="iconosAtencion"
                    alt="..."
                  />{" "}
                  <span>Ayuda</span>
                </div>
              </NavLink> */}
              {user?.user?.data?.Customers?.pshb && (
                <>
                  <div className="separadorTitulo" style={{ width: "100%" }} />
                  <div
                    className="navigate-button perfil-bottom"
                    activeclassname="active"
                    onClick={(e) => {
                      // Evita que el click cierre el drawer completo (igual que "Reintegros"
                      // más abajo): solo debe expandir/colapsar el grupo "Plan de Salud".
                      e.stopPropagation();
                      handleTogglePlanDeSalud();
                    }}
                  >
                    <div className="d-flex textoMenuLateral">
                      <div>
                        <img
                          src={IconPlanDeSalud}
                          className="iconosAtencion"
                          alt="Plan de Salud"
                          style={{ width: "24px", height: "24px" }}
                        />{" "}
                        <span>Plan de Salud</span>
                      </div>
                      <div style={{ marginLeft: "auto", marginRight: "11px" }}>
                        {isActivePlanDeSalud ? (
                          <FiChevronUp
                            size={20}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePlanDeSalud();
                            }}
                          />
                        ) : (
                          <FiChevronDown
                            size={20}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePlanDeSalud();
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {isActivePlanDeSalud && (
                  <>
                  <Row
                    style={{ marginLeft: 0 }}
                    onClick={(e) => {
                      // Igual patrón que el header de "Plan de Salud": toda la
                      // fila togglea el acordeón y no deja que el click cierre
                      // el drawer completo (PBI-24883).
                      e.stopPropagation();
                      handleToggleFacturacion();
                    }}
                  >
                    <NavLink
                      className="navigate-button perfil-bottom"
                      to={"/facturas"}
                      activeclassname="active"
                      style={{ width: "72%" }}
                    >
                      <div className="d-flex textoMenuLateral">
                        <span style={{ paddingLeft: "30px" }}>Facturación</span>
                      </div>
                    </NavLink>
                    <Col
                      style={{
                        margin: "auto",
                        marginLeft: "10px",
                      }}
                    >
                      {/* Sin onClick propio: el toggle ya lo maneja el onClick
                          del Row (bubbling); agregar uno aquí duplicaría el
                          toggle. */}
                      {isActiveFacturacion ? (
                        <FiChevronUp size={20} />
                      ) : (
                        <FiChevronDown size={20} />
                      )}
                    </Col>
                  </Row>
                  {isActiveFacturacion ? (
                    <>
                      <NavLink
                        className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                        style={{
                          fontSize: "20px",
                          width: "100%",
                        }}
                        to={"/debito-automatico"}
                        activeclassname="active"
                      >
                        <span style={{ paddingLeft: "30px" }}>
                          En tarjeta de crédito
                        </span>
                      </NavLink>
                      <NavLink
                        className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                        style={{
                          fontSize: "20px",
                          width: "100%",
                        }}
                        to={"/debito-cuenta"}
                        activeclassname="active"
                      >
                        <span style={{ paddingLeft: "30px" }}>
                          En cuenta bancaria
                        </span>
                      </NavLink>
                      <NavLink
                        className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                        style={{
                          fontSize: "20px",
                          width: "100%",
                        }}
                        to={"/historial-debito"}
                        activeclassname="active"
                      >
                        <span style={{ paddingLeft: "30px" }}>Historial</span>
                      </NavLink>
                    </>
                  ) : null}
                  <div className="separadorItem" />
                  <Row
                    style={{ marginLeft: 0 }}
                    onClick={(e) => {
                      // Igual patrón que el header de "Plan de Salud": toda la
                      // fila togglea el acordeón y no deja que el click cierre
                      // el drawer completo (PBI-24883).
                      e.stopPropagation();
                      handleToggleReintegro();
                    }}
                  >
                    <div
                      className="navigate-button"
                      activeclassname="active"
                      style={{ width: "72%" }}
                    >
                      <div className="d-flex textoMenuLateral">
                        <span style={{ paddingLeft: "30px" }}>Reintegros</span>
                      </div>
                    </div>
                    <Col
                      style={{
                        margin: "auto",
                        marginLeft: "10px",
                      }}
                    >
                      {/* Sin onClick propio: el toggle ya lo maneja el onClick
                          del Row (bubbling); agregar uno aquí duplicaría el
                          toggle. */}
                      {isActiveReintegro ? (
                        <FiChevronUp size={20} />
                      ) : (
                        <FiChevronDown size={20} />
                      )}
                    </Col>
                  </Row>
                  {isActiveReintegro ? (
                    <>
                      <NavLink
                        className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                        style={{
                          fontSize: "20px",
                          width: "100%",
                        }}
                        to={"/reintegro-estado"}
                        activeclassname="active"
                      >
                        <span style={{ paddingLeft: "30px" }}>Estado</span>
                      </NavLink>
                      <NavLink
                        className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                        style={{
                          fontSize: "20px",
                          width: "100%",
                        }}
                        to={"/reintegro-solicitud"}
                        activeclassname="active"
                      >
                        <span style={{ paddingLeft: "30px" }}>Solicitud</span>
                      </NavLink>
                    </>
                  ) : null}
                  <div className="separadorItem" />
                  <Row style={{ marginLeft: 0 }}>
                    <NavLink
                      className="navigate-button"
                      to="/credencial-digital"
                      activeclassname="active"
                      style={{ width: "72%" }}
                    >
                      <div className="d-flex textoMenuLateral">
                        <span style={{ paddingLeft: "30px" }}>Credencial</span>
                      </div>
                    </NavLink>
                  </Row>
                  </>
                  )}
                </>
              )}

              <div className="separadorTitulo" style={{ width: "100%" }} />
              <NavLink
                className="navigate-button"
                to="/cerrar-sesion"
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>logout</span>{" "}
                  <span>Cerrar sesión</span>
                </div>
              </NavLink>
            </div>
          </div>
        </aside>
      </div>
    </Box>
  );
  return (
    <div>
      {["left"].map((anchor) => (
        <React.Fragment key={anchor}>
          <i
            class="bi bi-list"
            onClick={toggleDrawer(anchor, true)}
            style={{ fontSize: "30px", color: "#0062C1" }}
          ></i>
          <Drawer
            anchor={anchor}
            open={state[anchor]}
            onClose={toggleDrawer(anchor, false)}
          >
            {list(anchor)}
          </Drawer>
        </React.Fragment>
      ))}
    </div>
  );
};

export default DrawerHB;
